import { useEffect, useRef, useState } from "react";

interface UseGlitchOptions {
  /** Steady-state value to display when not in a glitch burst */
  target: string;
  /** Master switch — when false, display mirrors target and the loop is idle */
  enabled: boolean;
  /** Function returning a scrambled version of target. Must be stable (module-level). */
  scramble: (target: string) => string;
  minDelayMs?: number;
  maxDelayMs?: number;
  burstMs?: number;
  frameMs?: number;
}

/**
 * Quem pede "reduzir movimento" no sistema não recebe o glitch: o texto embaralha
 * várias vezes por segundo durante o burst, que é exatamente o tipo de movimento
 * que essa preferência pede para evitar. Fica sempre o texto final, legível.
 */
function prefereMenosMovimento(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Periodic glitch loop: every minDelay–maxDelay ms, runs a burst where
 * `display` rapidly cycles through scramble(target) for burstMs.
 * Pair with the CSS class `glitch-text` + `glitch-text--active`.
 */
export function useGlitch({
  target,
  enabled,
  scramble,
  minDelayMs = 4000,
  maxDelayMs = 7000,
  burstMs = 400,
  frameMs = 50,
}: UseGlitchOptions): { display: string; isGlitching: boolean } {
  const [display, setDisplay] = useState(target);
  const [isGlitching, setIsGlitching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror target while idle (e.g., during count-up phase)
  useEffect(() => {
    if (!enabled) setDisplay(target);
  }, [target, enabled]);

  useEffect(() => {
    if (!enabled || prefereMenosMovimento()) return;

    const scheduleNext = () => {
      const delay = minDelayMs + Math.random() * (maxDelayMs - minDelayMs);
      timerRef.current = setTimeout(() => {
        setIsGlitching(true);
        const totalFrames = Math.max(1, Math.floor(burstMs / frameMs));
        let frame = 0;

        const tick = () => {
          frame++;
          if (frame < totalFrames) {
            setDisplay(scramble(target));
            timerRef.current = setTimeout(tick, frameMs);
          } else {
            setDisplay(target);
            setIsGlitching(false);
            scheduleNext();
          }
        };
        timerRef.current = setTimeout(tick, frameMs);
      }, delay);
    };

    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, target, scramble, minDelayMs, maxDelayMs, burstMs, frameMs]);

  return { display, isGlitching };
}
